import { useInvoice, useMelioIntl } from '@melio/ar-domain';
import { Modal } from '@melio/penny';
import { useAnalyticsView } from '@melio/platform-analytics';
import { forwardRef } from '@melio/platform-utils';

export type DeleteInvoiceModalActivityProps = {
  onError?: ErrorFunction;
  onClose: VoidFunction;
  onDone?: VoidFunction;
  invoiceId?: string;
};

export const DeleteInvoiceModalActivity = forwardRef<DeleteInvoiceModalActivityProps>(
  ({ invoiceId, onClose, onError, onDone = onClose, ...props }, ref) => {
    const {
      data: invoice,
      isLoading,
      delete: deleteInvoice,
      isMutating: isDeletingInvoice,
    } = useInvoice({ id: invoiceId, onError });
    const { formatMessage } = useMelioIntl();
    const onDeleteInvoice = () => void deleteInvoice().then(onDone).catch(onError);

    useAnalyticsView('Invoice', !isLoading, true, {
      ProductName: 'ar',
      PageName: 'delete-invoice',
      Flow: 'delete-invoice',
      Intent: 'delete-invoice',
      InvoiceId: invoice?.id,
      InvoiceStatus: invoice?.displayStatus,
      DueDate: invoice?.dueDate,
    });

    return (
      <Modal
        data-testid={`delete-invoice-modal-activity-${invoiceId ?? ''}`}
        data-component={DeleteInvoiceModalActivity.displayName}
        header={formatMessage('ar.invoiceLifecycle.activities.deleteInvoice.modal.header.text', {
          invoiceNumber: invoice?.invoiceNumber,
        })}
        description={formatMessage('ar.invoiceLifecycle.activities.deleteInvoice.modal.description.text')}
        isLoading={isLoading}
        isOpen={!!invoiceId}
        onClose={() => !isDeletingInvoice && onClose()}
        shouldHideCloseButton={isDeletingInvoice}
        secondaryButton={{
          isDisabled: isDeletingInvoice,
          label: formatMessage('ar.invoiceLifecycle.activities.deleteInvoice.modal.buttons.cancel.label'),
          onClick: onClose,
          variant: 'tertiary',
        }}
        primaryButton={{
          isLoading: isDeletingInvoice,
          label: formatMessage('ar.invoiceLifecycle.activities.deleteInvoice.modal.buttons.confirm.label'),
          variant: 'critical',
          onClick: onDeleteInvoice,
        }}
        {...props}
        ref={ref}
      />
    );
  }
);

DeleteInvoiceModalActivity.displayName = 'DeleteInvoiceModalActivity';
